import { useEffect, useMemo, useState } from "react";

import {
  extractSectionsFromDigestData,
  transformDigestDataToAccounts,
} from "../Container/WatchlistSummary";
import {
  useAccountHawkeyeMetrics,
  useAccountKpiDisplay,
} from "../Pages/AccountWorkspace/components/Kpis";
import { useAppDispatch, useAppSelector } from "../Store/Hooks";
import {
  fetchAccountTeam,
  fetchCustomerTeam,
  fetchExecutiveTeamDetail,
  fetchStakeholderRelationships,
} from "../Store/Reducers/AccountDetailsSlice";
import {
  clearAccountSummary,
  fetchAccountSummary,
} from "../Store/Reducers/AccountWorkspaceSlice";
import { generateWatchListBriefing } from "../Store/Reducers/BriefDocumentSlice";
import type {
  WatchlistSummaryAccount,
  WatchlistSummarySection,
} from "../Types/WatchlistSummary";

type HealthFlag = "R" | "Y" | "G";
type RelevanceFlag = "H" | "M" | "L";

export type PublicSectionDot = {
  id: string;
  label: string;
  healthFlag: HealthFlag;
  relevanceFlag: RelevanceFlag;
  healthScore?: number;
  relevanceScore?: number;
};

const normalizeHealthFlag = (flag?: string | null): HealthFlag => {
  const normalized = String(flag ?? "").toUpperCase();
  if (normalized === "R" || normalized === "G") return normalized;
  return "Y";
};

const normalizeRelevanceFlag = (flag?: string | null): RelevanceFlag => {
  const normalized = String(flag ?? "").toUpperCase();
  if (normalized === "H" || normalized === "L") return normalized;
  return "M";
};

const parsePublicBriefingAccount = (
  payload: any,
  accountId: string
): {
  account: WatchlistSummaryAccount | null;
  sections: WatchlistSummarySection[];
  date: string | null;
} => {
  const root = payload?.content ?? payload?.data ?? payload;
  const results = Array.isArray(root?.results)
    ? root.results
    : Array.isArray(payload?.results)
      ? payload.results
      : [];

  const rawDigests = results.flatMap((item: any) => {
    if (Array.isArray(item?.digests) && item.digests.length > 0) {
      return item.digests.map((digest: any) => ({
        ...digest,
        account_id: digest?.account_id || item?.account_id,
        account_name:
          digest?.account_name || item?.customer_name || item?.account_name,
      }));
    }
    if (item?.data?.summary || item?.summary) {
      return [
        {
          account_id: item.account_id,
          account_name: item.customer_name || item.account_name,
          date: item.date || root?.date,
          data: item.data || { summary: item.summary },
        },
      ];
    }
    return [];
  });

  const briefingData = rawDigests.map((digest: any) => {
    if (digest?.data?.summary) return digest;
    if (digest?.summary) {
      return {
        ...digest,
        data: {
          ...(digest.data || {}),
          summary: digest.summary,
        },
      };
    }
    return digest;
  });

  if (!briefingData.length) {
    return {
      account: null,
      sections: [],
      date: root?.date || payload?.date || null,
    };
  }

  const orgName =
    root?.organization_name ||
    payload?.organization_name ||
    "Your Organization Name";
  const accounts = transformDigestDataToAccounts(briefingData as any, orgName);
  const account =
    accounts.find(item => String(item.accountId) === String(accountId)) ||
    accounts[0] ||
    null;
  const sections = extractSectionsFromDigestData(briefingData as any);

  return {
    account,
    sections,
    date: root?.date || payload?.date || briefingData[0]?.date || null,
  };
};

const mapAccountToPublicDots = (
  account: WatchlistSummaryAccount | null,
  sections: WatchlistSummarySection[]
): PublicSectionDot[] => {
  if (!account) return [];
  if (
    !account.overallData &&
    !(account.sectionsData && account.sectionsData.length > 0)
  ) {
    return [];
  }

  const dots: PublicSectionDot[] = [];
  const overall = account.overallData;
  if (overall) {
    dots.push({
      id: "overall",
      label: "Overall",
      healthFlag: normalizeHealthFlag(
        overall.overallHealthFlag || overall.overallFlag
      ),
      relevanceFlag: normalizeRelevanceFlag(overall.overallRelevanceFlag),
      healthScore: overall.overallHealthScore ?? overall.overallScore,
      relevanceScore: overall.overallRelevanceScore,
    });
  }

  const byName = new Map(
    (account.sectionsData || []).map(section => [section.section_name, section])
  );

  const orderedSections =
    sections.length > 0
      ? [...sections].sort((a, b) => a.display_order - b.display_order)
      : (account.sectionsData || []).map(section => ({
          id: section.section_name,
          name: section.section_name,
          display_order: 0,
          enabled: true,
        }));

  orderedSections.forEach(sectionMeta => {
    const section = byName.get(sectionMeta.id);
    if (!section) return;
    dots.push({
      id: sectionMeta.id,
      label: sectionMeta.name || sectionMeta.id,
      healthFlag: normalizeHealthFlag(
        section.sectionHealthFlag || section.sectionFlag
      ),
      relevanceFlag: normalizeRelevanceFlag(section.sectionRelevanceFlag),
      healthScore: section.sectionHealthScore,
      relevanceScore: section.sectionRelevanceScore,
    });
  });

  return dots;
};

export const useAccountSynopsisData = (accountId: string | undefined) => {
  const dispatch = useAppDispatch();
  const {
    accountSummary,
    loading: summaryLoading,
    error: summaryError,
  } = useAppSelector(state => state.accountWorkspace);
  const { accountTeam, customerTeam, executiveTeam, stakeholderRelationships } =
    useAppSelector(state => state.accountDetails);

  const [publicBriefingAccount, setPublicBriefingAccount] =
    useState<WatchlistSummaryAccount | null>(null);
  const [publicBriefingSections, setPublicBriefingSections] = useState<
    WatchlistSummarySection[]
  >([]);
  const [publicBriefingDate, setPublicBriefingDate] = useState<string | null>(
    null
  );
  const [briefLoading, setBriefLoading] = useState(false);

  const accountName = accountSummary?.account_name ?? null;
  const hawkeye = useAccountHawkeyeMetrics({ accountId, accountName });
  const kpiDisplay = useAccountKpiDisplay({ hawkeye });

  useEffect(() => {
    if (!accountId) return;

    dispatch(fetchAccountSummary(accountId));
    dispatch(fetchAccountTeam(accountId));
    dispatch(fetchCustomerTeam(accountId));
    dispatch(fetchExecutiveTeamDetail(accountId));
    dispatch(fetchStakeholderRelationships(accountId));

    let cancelled = false;
    setBriefLoading(true);
    setPublicBriefingAccount(null);
    setPublicBriefingSections([]);
    setPublicBriefingDate(null);

    dispatch(generateWatchListBriefing({ account_ids: [accountId] }))
      .unwrap()
      .then(response => {
        if (cancelled) return;
        const { account, sections, date } = parsePublicBriefingAccount(
          response,
          accountId
        );
        setPublicBriefingAccount(account);
        setPublicBriefingSections(sections);
        setPublicBriefingDate(date);
      })
      .catch(() => {
        if (!cancelled) {
          setPublicBriefingAccount(null);
          setPublicBriefingSections([]);
          setPublicBriefingDate(null);
        }
      })
      .finally(() => {
        if (!cancelled) setBriefLoading(false);
      });

    return () => {
      cancelled = true;
      dispatch(clearAccountSummary());
    };
  }, [accountId, dispatch]);

  const publicSectionDots = useMemo(
    () => mapAccountToPublicDots(publicBriefingAccount, publicBriefingSections),
    [publicBriefingAccount, publicBriefingSections]
  );

  const publicSynopsisText = useMemo(() => {
    if (!publicBriefingAccount) return "";
    const overall = publicBriefingAccount.overallData;
    const statement =
      publicBriefingAccount.accountBriefLine ||
      overall?.summaryStatement ||
      overall?.relevanceStatement ||
      "";
    return typeof statement === "string" ? statement.trim() : "";
  }, [publicBriefingAccount]);

  const publicDataDate = useMemo(() => {
    if (!publicBriefingDate) return null;
    const parsed = new Date(publicBriefingDate);
    if (Number.isNaN(parsed.getTime())) return publicBriefingDate;
    return parsed.toLocaleDateString("en-US", {
      month: "short",
      day: "numeric",
      year: "numeric",
    });
  }, [publicBriefingDate]);

  const overallScore = useMemo(() => {
    const digestMeta = hawkeye.matrixData[0]?.digestMeta;
    const fromWeighted = digestMeta?.overall_weighted_score;
    if (fromWeighted != null && Number.isFinite(fromWeighted)) {
      return Math.round(fromWeighted);
    }
    const fromOverall = digestMeta?.overall_score;
    if (fromOverall != null && Number.isFinite(fromOverall)) {
      return Math.round(fromOverall);
    }
    return null;
  }, [hawkeye.matrixData]);

  const teamsLoading = accountTeam.loading || executiveTeam.loading;

  const executiveSponsors = useMemo(
    () => [...executiveTeam.sponsors, ...executiveTeam.extraSponsors],
    [executiveTeam.extraSponsors, executiveTeam.sponsors]
  );

  return {
    accountSummary,
    summaryLoading,
    summaryError,
    briefLoading,
    publicBriefingAccount,
    publicBriefingSections,
    publicSectionDots,
    publicSynopsisText,
    publicDataDate,
    overallScore,
    kpiDisplay,
    hawkeye,
    teamsLoading,
    customerMembers: customerTeam.members,
    customerLoading: customerTeam.loading,
    accountTeamMembers: accountTeam.members,
    executiveSponsors,
    relationships: stakeholderRelationships.items,
    relationshipsLoading: stakeholderRelationships.loading,
  };
};
